/**
 * THE OPS RATE CARD, AS DATA. Copied from what the business publishes, never authored here.
 *
 * Why a copy exists at all. The published page is the contract, exactly as it is for a customer,
 * and `https://faithfullykept.com/pricing/` is where it lives. That page belongs to the website,
 * which is a separate repository with a separate agent, so a shared code package is not available
 * to us. `erd.md` Part 2 settled the alternative: ops holds its own card definition as data,
 * content-hashed, `card_versions` stamps which revision priced each quote, agreement and visit,
 * and a nightly check fetches the published page and fails loudly when the two disagree. That
 * tests the actual business promise, the way a customer would, with no repository coupling.
 *
 * THREE RULES ABOUT THIS FILE, AND THEY ARE THE PRODUCT.
 *
 * 1. Nothing here is invented. Every figure below appears on the published page today, and the
 *    row and column labels are copied verbatim so the nightly check can match on them.
 * 2. There is no discount, coupon, override or rounding anywhere in this file or downstream of it.
 *    There is no discount in this business, so there is nowhere to type one.
 * 3. Editing a price here does not change a price. It changes what ops believes the website says,
 *    which the nightly check will then contradict. A price changes on the website first.
 *
 * The line codes are stable across price changes: a code names a row and a column, not a figure.
 * That is what lets an agreement point at `clean.4b3b.every_2_weeks` for three years while the
 * card revision underneath it moves.
 */

/** The four captioned tables on the published page, verbatim, plus the flat add-on list. */
export const PUBLISHED_TABLES = Object.freeze([
  'House cleaning, per visit',
  'Deep clean and move-out, one-off',
  'Home watch, per visit',
  'Turnover cleaning, per turn',
] as const);
export type PublishedTable = (typeof PUBLISHED_TABLES)[number];

export const ADD_ON_HEADING = 'Add-ons, flat rate';

/**
 * The heading the published page puts the scope under, and the two headings inside it.
 *
 * The page publishes what a clean covers in the same breath as what it costs, and the job sheet
 * freezes a copy of those words: a client who reads the page and a cleaner who reads the sheet
 * have to be reading one promise. So the nightly check reads this section as well as the tables,
 * and these are the anchors it reads it by.
 */
export const SCOPE_HEADING = 'What a clean covers';
export const SCOPE_SECTION_HEADINGS = Object.freeze([
  'What every visit includes',
  'What it does not include',
] as const);

export interface RateLine {
  /** Stable ops identifier. Rows point at this, never at a figure. */
  readonly code: string;
  /** Which published table or list the line is read from. */
  readonly source: PublishedTable | typeof ADD_ON_HEADING;
  /** The row label as published, verbatim, including spacing and punctuation. */
  readonly rowLabel: string;
  /** The column heading as published, verbatim. The add-on list has one implicit column. */
  readonly columnLabel: string;
  /** Cents. Null means the page says the figure is quoted after a walkthrough. */
  readonly priceCents: number | null;
  /** True where the page publishes "from", so the figure is a floor and not the price. */
  readonly isFrom?: boolean;
}

const HOUSE_ROWS = [
  { code: '1b1b', label: '1 bed / 1 bath', prices: [15500, 13600, 12400, 11600] },
  { code: '2b1b', label: '2 bed / 1 bath', prices: [17500, 15400, 14000, 13100] },
  { code: '2b2b', label: '2 bed / 2 bath', prices: [19500, 17200, 15600, 14600] },
  { code: '3b2b', label: '3 bed / 2 bath', prices: [22500, 19800, 18000, 16900] },
  { code: '4b3b', label: '4 bed / 3 bath', prices: [26500, 23300, 21200, 19900] },
  { code: '5b3b', label: '5 bed / 3+ bath', prices: [31000, 27300, 24800, 23300] },
] as const;

const HOUSE_COLUMNS = [
  { code: 'one_time', label: 'One time' },
  { code: 'every_4_weeks', label: 'Every 4 weeks' },
  { code: 'every_2_weeks', label: 'Every 2 weeks' },
  { code: 'weekly', label: 'Weekly' },
] as const;

/**
 * The first visit of a new client is priced as a deep clean when the home has not been
 * professionally cleaned in the last ninety days, and the business brief is explicit that this is
 * said before booking and never on the invoice afterwards. The page publishes the resulting
 * figures as their own table, so ops copies the figures rather than recomputing the multiplier.
 * A derived number and a published number that disagree is the failure this whole design avoids.
 */
const ONE_OFF_ROWS = [
  { code: '1b1b', label: '1 bed / 1 bath', deep: 25000, moveOut: 29500 },
  { code: '2b1b', label: '2 bed / 1 bath', deep: 28000, moveOut: 30600 },
  { code: '2b2b', label: '2 bed / 2 bath', deep: 31200, moveOut: 34100 },
  { code: '3b2b', label: '3 bed / 2 bath', deep: 36000, moveOut: 39400 },
  { code: '4b3b', label: '4 bed / 3 bath', deep: 42400, moveOut: 46400 },
  { code: '5b3b', label: '5 bed / 3+ bath', deep: 49600, moveOut: 54300 },
] as const;

const HOME_WATCH_ROWS = [
  { code: 'up_to_1500', label: 'Condo or villa, up to 1,500 sq ft', price: 6900 },
  { code: '1501_2500', label: 'Home, 1,501 to 2,500 sq ft', price: 8500 },
  { code: '2501_3500', label: 'Home, 2,501 to 3,500 sq ft', price: 9900 },
  { code: '3501_5000', label: 'Home, 3,501 to 5,000 sq ft', price: 11900 },
  { code: 'over_5000', label: 'Over 5,000 sq ft', price: null },
] as const;

const TURNOVER_ROWS = [
  { code: 'studio_or_1bed', label: 'Studio or 1 bed', price: 11900 },
  { code: '2bed', label: '2 bed', price: 15900 },
  { code: '3bed', label: '3 bed', price: 20500 },
  { code: '4bed', label: '4 bed', price: 25900 },
  { code: '5plus', label: '5+ bed', price: null },
] as const;

const ADD_ON_ROWS = [
  { code: 'inside_oven', label: 'Inside oven', price: 3500, isFrom: false },
  { code: 'inside_fridge', label: 'Inside fridge', price: 3500, isFrom: false },
  { code: 'interior_windows', label: 'Interior windows', price: 6000, isFrom: true },
  // The page writes the qualifier inside the label, so the label is 'Laundry (per load)'.
  // Copied verbatim: the comparison matches on the label a customer reads.
  { code: 'laundry_per_load', label: 'Laundry (per load)', price: 2500, isFrom: false },
  { code: 'garage_sweep', label: 'Garage sweep', price: 4500, isFrom: false },
] as const;

function buildLines(): RateLine[] {
  const lines: RateLine[] = [];

  for (const row of HOUSE_ROWS) {
    HOUSE_COLUMNS.forEach((column, index) => {
      lines.push({
        code: `clean.${row.code}.${column.code}`,
        source: 'House cleaning, per visit',
        rowLabel: row.label,
        columnLabel: column.label,
        priceCents: row.prices[index] ?? null,
      });
    });
  }
  // The page publishes a sixth row saying larger homes are quoted after a walkthrough. It is a
  // real published line and ops carries it, priced null, so a quote against it is legible rather
  // than an unexplained absence.
  for (const column of HOUSE_COLUMNS) {
    lines.push({
      code: `clean.6plus.${column.code}`,
      source: 'House cleaning, per visit',
      rowLabel: '6+ bed',
      columnLabel: column.label,
      priceCents: null,
    });
  }

  for (const row of ONE_OFF_ROWS) {
    lines.push({
      code: `deep.${row.code}`,
      source: 'Deep clean and move-out, one-off',
      rowLabel: row.label,
      columnLabel: 'Deep clean',
      priceCents: row.deep,
    });
    lines.push({
      code: `moveout.${row.code}`,
      source: 'Deep clean and move-out, one-off',
      rowLabel: row.label,
      columnLabel: 'Move-out or move-in',
      priceCents: row.moveOut,
    });
  }

  for (const row of HOME_WATCH_ROWS) {
    lines.push({
      code: `watch.${row.code}`,
      source: 'Home watch, per visit',
      rowLabel: row.label,
      columnLabel: 'Per visit',
      priceCents: row.price,
    });
  }

  for (const row of TURNOVER_ROWS) {
    lines.push({
      code: `turn.${row.code}`,
      source: 'Turnover cleaning, per turn',
      rowLabel: row.label,
      columnLabel: 'Per turn',
      priceCents: row.price,
    });
  }

  for (const row of ADD_ON_ROWS) {
    lines.push({
      code: `addon.${row.code}`,
      source: ADD_ON_HEADING,
      rowLabel: row.label,
      columnLabel: 'Flat',
      priceCents: row.price,
      isFrom: row.isFrom,
    });
  }

  return lines;
}

export const RATE_CARD_SOURCE_URL = 'https://faithfullykept.com/pricing/';

/**
 * The revision in force, bumped by hand only after the page changes. `card_versions` holds one row
 * per revision with an effective range and its hash, and every agreement names the row it was
 * signed under, so a three-year-old agreement is still priced against the card that was in force
 * when it was signed. Without that, a `card_version` stamp resolves to nothing and every historical
 * price is trivially correct, which is worse than no check at all.
 */
export const RATE_CARD_REVISION = 1;

/** The date this revision was read off the published page and verified against it. */
export const RATE_CARD_CAPTURED_ON = '2026-09-03';

/**
 * EVERY REVISION'S LINES, BY REVISION. Only revision 1 exists yet.
 *
 * The next capture keeps the old revision's lines beside the new ones: it adds its own lines under
 * the next number and bumps `RATE_CARD_REVISION`, and it never edits or removes the lines of a
 * revision already shipped. An agreement signed under revision 1 had its first visit said to the
 * client at revision 1's figure, and every visit of it is judged by revision 1 for as long as the
 * row exists; a revision whose lines were gone would leave all of them with nothing to be priced by.
 */
const LINES_BY_REVISION: ReadonlyMap<number, readonly RateLine[]> = new Map([
  [1, Object.freeze(buildLines())],
]);

const BY_REVISION_AND_CODE = new Map(
  [...LINES_BY_REVISION].map(([revision, lines]): [number, Map<string, RateLine>] => [
    revision,
    new Map(lines.map((line) => [line.code, line])),
  ]),
);

/** The lines of one revision, or undefined for a revision this code was never given. */
export function rateCardLinesOf(revision: number): readonly RateLine[] | undefined {
  return LINES_BY_REVISION.get(revision);
}

/** One line of one revision, by its code. Undefined when either is not in this file. */
export function rateLineOf(revision: number, code: string): RateLine | undefined {
  return BY_REVISION_AND_CODE.get(revision)?.get(code);
}

function linesInForce(): readonly RateLine[] {
  const lines = rateCardLinesOf(RATE_CARD_REVISION);
  if (lines === undefined) {
    throw new Error(`The card in force is revision ${RATE_CARD_REVISION}, and it has no lines.`);
  }
  return lines;
}

/** The card in force, which a new quote or agreement is priced from. */
export const RATE_CARD_LINES: readonly RateLine[] = linesInForce();

export const RATE_LINE_CODES: readonly string[] = Object.freeze(
  RATE_CARD_LINES.map((line) => line.code),
);

/** A line of the card in force. A row signed under its own revision is read with `rateLineOf`. */
export function rateLine(code: string): RateLine | undefined {
  return rateLineOf(RATE_CARD_REVISION, code);
}

/**
 * The canonical serialisation the content hash is taken over. Sorted by code and written field by
 * field, so a reordering of the source file cannot change the hash and a changed figure always
 * does. Anything hashing a JSON dump of an object literal is hashing key order as well as content.
 */
export function canonicalCard(lines: readonly RateLine[] = RATE_CARD_LINES): string {
  return [...lines]
    .sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0))
    .map((line) =>
      [
        line.code,
        line.source,
        line.rowLabel,
        line.columnLabel,
        line.priceCents === null ? 'quoted' : String(line.priceCents),
        line.isFrom ? 'from' : 'exact',
      ].join('|'),
    )
    .join('\n');
}

/** SHA-256 of the canonical form, hex. WebCrypto, so the Worker and the tests agree byte for byte. */
export async function cardContentHash(
  lines: readonly RateLine[] = RATE_CARD_LINES,
): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalCard(lines));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Cents to the string a person reads. No rounding: a fractional cent would be a defect. */
export function formatCents(cents: number): string {
  if (!Number.isInteger(cents)) throw new Error(`price is not whole cents: ${cents}`);
  const sign = cents < 0 ? '-' : '';
  const absolute = Math.abs(cents);
  return `${sign}$${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, '0')}`;
}
